import { useLocation } from "react-router-dom";
import { useEffect, useRef } from "react";
import { syncBeverageUserSessionForAcid } from "../utils/beverageSession";

function getAcidFromLocation(location) {
  const searchParams = new URLSearchParams(location.search);
  return searchParams.get("acid");
}

export function getAcidFromCookie(cookieName) {
  const cookies = document.cookie.split(";");

  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split("=");
    if (name === cookieName) {
      try {
        const decodedCookieValue = decodeURIComponent(value);
        if (decodedCookieValue) {
          return decodedCookieValue;
        } else {
          console.log("dtm_user_id cookie value is empty");
          return null;
        }
      } catch (error) {
        console.error("Failed to parse dtm_user_id cookie:", error);
        return null;
      }
    }
  }
  return null;
}

export const acidRegex = /^[A-F0-9]{33}\.[A-Z0-9]{2}$/;

/**
 * Returns the first validly formatted ACID, skipping invalid values so they
 * can't override a valid fallback. Invalid candidates are collected for
 * separate logging, and the function is exported to allow its precedence
 * behavior to be unit tested.
 */
export function resolveValidAcid(candidates) {
  const invalid = [];
  for (const { value, source, stopOnInvalid = false } of candidates) {
    if (!value) {
      continue;
    }
    if (acidRegex.test(value)) {
      return { acid: value, invalid };
    }
    invalid.push({ value, source });
    if (stopOnInvalid) {
      return { acid: null, invalid };
    }
  }
  return { acid: null, invalid };
}

export const useAcid = () => {
  const location = useLocation();
  const acidFromLocation = getAcidFromLocation(location);

  const { acid, invalid } = resolveValidAcid([
    { value: acidFromLocation, source: "URL param", stopOnInvalid: true },
    { value: getAcidFromCookie("dtm_user_id"), source: "dtm_user_id cookie" },
    { value: sessionStorage.getItem("acid"), source: "sessionStorage" },
  ]);

  /**
   * Logs each unique invalid ACID only once per page load, preventing repeated
   * warnings from normal re-renders.
   */
  const loggedInvalidRef = useRef(new Set());
  useEffect(() => {
    invalid.forEach(({ value, source }) => {
      const key = `${source}:${value}`;
      if (!loggedInvalidRef.current.has(key)) {
        loggedInvalidRef.current.add(key);
        console.error(`Invalid ACID format from ${source}:`, value);
      }
    });
  });

  let authState = "anonymous";
  if (acid) {
    authState = "remembered";
    if (!acidFromLocation) {
      authState = getAcidFromCookie("s_loginState") || authState;
    }
  }

  useEffect(() => {
    const acidFromStorage = sessionStorage.getItem("acid");
    syncBeverageUserSessionForAcid(acid, sessionStorage, acidFromStorage);
    if (acidFromStorage !== acid) {
      // When acid changes, clear the old access_token as it was generated for a different acid
      if (acidFromStorage) {
        sessionStorage.removeItem("access_token");
      }
      /**
       * Remove the key instead of storing null, since sessionStorage converts
       * values to strings and would persist the literal "null".
       */
      if (acid) {
        sessionStorage.setItem("acid", acid);
      } else {
        sessionStorage.removeItem("acid");
      }
    }
  }, [acid]);

  return { acid, authState };
};
